import { gql } from 'graphql-request';
import { del, get, patch, post, put } from '@elementary/common-ui/utils/http';
import type { SummaryResponse } from 'incidents/api';
import type { Ticket } from 'incidents/types';
import type {
  MergeIncidentsV2Response,
  SplitIncidentV2Response,
  IncidentV2TicketRow,
  IncidentsSettings,
} from './types';

export const getIncidentDetailQuery = gql`
  query GetIncidentDetail($id: String!) {
    incidentV2(id: $id) {
      id
      title
      mergedIntoId
      status
      severity
      assignee
      startTime
      resolvedAt
      lastFailureTime
      attributes {
        id
        displayName
        data {
          __typename
          ... on DatetimeAttributeData {
            dateValue: value
          }
          ... on StringAttributeData {
            stringValue: value
          }
        }
      }
      monitors {
        test {
          id
          name
          type
          description
          owners
          subscribers
        }
        latestTestExecution {
          id
          status
          executionTimestamp
          columnName
          subType
          subTestUniqueId
        }
        asset {
          __typename
          id
          name
          fqn
          type
        }
        signal {
          id
          startTime
          endTime
          lastFailureTime
          failureCount
          lastStatus
          isActive
        }
      }
      summaries {
        id
        incidentId
        summaryType
        content
        createdAt
        updatedAt
      }
      events {
        id
        incidentId
        eventTime
        type
        data
        actor {
          id
          email
          name
        }
      }
    }
  }
`;

export const getIncidentLineageQuery = gql`
  query GetIncidentLineage($id: String!, $upstream: Int!, $downstream: Int!) {
    incidentV2(id: $id) {
      id
      lineage(upstream: $upstream, downstream: $downstream) {
        rootNodeIds
        lineage {
          assets {
            nodes {
              __typename
              id
              parentId
              name
              fqn
              type
              artifactType
              displayType
              sourceType
              level
              tags
              owners
              path
              ... on TableAsset {
                dataPlatform
              }
              ... on BiAsset {
                platform
                biType
              }
              ... on SemanticAsset {
                platform
                semanticType
              }
            }
            edges {
              src
              dst
            }
            boundaries {
              upstream
              downstream
            }
          }
          columns {
            nodes {
              __typename
              ... on Asset {
                id
                parentId
                name
                artifactType
                displayType
                sourceType
                level
                tags
                owners
                path
                ... on ColumnAsset {
                  displayName
                }
              }
            }
            edges {
              src
              dst
            }
            exceededLimitParentAssets
            boundaries {
              upstream
              downstream
            }
          }
        }
      }
    }
  }
`;

export const getIncidentSummaryByIdQuery = gql`
  query GetIncidentSummaryById($id: String!) {
    incidentV2(id: $id) {
      id
      title
    }
  }
`;

export const getIncidentsPagedQuery = gql`
  query GetIncidentsPaged(
    $filters: IncidentV2BaseFilters
    $sort: IncidentV2SortFieldPaginationSort
    $cursor: String
    $limit: Int
    $incidentFilters: [Filter!]
  ) {
    incidentsV2Paged(
      filters: $filters
      sort: $sort
      cursor: $cursor
      limit: $limit
      incidentFilters: $incidentFilters
    ) {
      items {
        id
        title
        status
        severity
        assignee
        startTime
        resolvedAt
        lastFailureTime
        databases
        schemas
        monitors {
          signal {
            lastStatus
            isActive
            endTime
          }
        }
      }
      pageInfo {
        hasNextPage
        nextCursor
      }
    }
  }
`;

export const getIncidentsSummaryQuery = gql`
  query GetIncidentsSummary(
    $filters: IncidentV2BaseFilters
    $incidentFilters: [Filter!]
  ) {
    incidentsV2Summary(filters: $filters, incidentFilters: $incidentFilters) {
      total
      byStatus {
        open
        acknowledged
        resolved
      }
      bySeverity {
        low
        normal
        high
        critical
      }
    }
  }
`;

export const getIncidentsFilterOptionsQuery = gql`
  query GetIncidentsFilterOptions(
    $filters: IncidentV2FilterOptionsFilters
    $incidentFilters: [Filter!]
  ) {
    incidentsV2FilterOptions(
      filters: $filters
      incidentFilters: $incidentFilters
    ) {
      field
      options {
        value
        count
      }
    }
  }
`;

export const getIncidentsForMergeQuery = gql`
  query GetIncidentsForMerge(
    $filters: IncidentV2BaseFilters
    $cursor: String
    $limit: Int
  ) {
    incidentsV2Paged(filters: $filters, cursor: $cursor, limit: $limit) {
      items {
        id
        title
        status
        severity
        assignee
        startTime
        lastFailureTime
        monitors {
          test {
            name
          }
          asset {
            fqn
            name
          }
          signal {
            lastStatus
          }
        }
        events {
          id
        }
      }
      pageInfo {
        hasNextPage
        nextCursor
      }
    }
  }
`;

export const fetchIncidentV2Tickets = (
  envId: string,
  incidentId: string,
): Promise<IncidentV2TicketRow[]> =>
  get(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/tickets`,
  );

export const fetchIncidentsSettings = (
  envId: string,
): Promise<IncidentsSettings> =>
  get(`${window.VITE_API_BASE_URL}/${envId}/incidents/settings/`);

export const updateIncidentsSettings = (
  envId: string,
  settings: IncidentsSettings,
): Promise<IncidentsSettings> =>
  put(`${window.VITE_API_BASE_URL}/${envId}/incidents/settings/`, settings);

export const createIncidentV2Ticket = (
  envId: string,
  incidentId: string,
  incidentUrl: string,
  integrationId: string,
  customFields?: Record<string, unknown>,
): Promise<Ticket> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/ticket`,
    {
      incident_url: incidentUrl,
      ticketing_integration_id: integrationId,
      custom_fields: customFields,
    },
  );

export const detachIncidentV2Ticket = (
  envId: string,
  incidentId: string,
  serviceTicketId: string,
): Promise<void> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/tickets/detach`,
    { service_ticket_id: serviceTicketId },
  );

export const attachIncidentV2Ticket = (
  envId: string,
  incidentId: string,
  ticketingIntegrationId: string,
  serviceTicketId: string,
  serviceTicketUrl: string,
): Promise<IncidentV2TicketRow> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/tickets/attach`,
    {
      ticketing_integration_id: ticketingIntegrationId,
      service_ticket_id: serviceTicketId,
      service_ticket_url: serviceTicketUrl,
    },
  );

export const fetchDefaultIncidentV2Fields = (
  envId: string,
  incidentId: string,
  integrationId: string,
  incidentUrl: string,
): Promise<{
  default_fields: {
    fields: Record<string, any>;
  };
}> =>
  get(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/ticket/default-fields`,
    {
      ticketing_integration_id: integrationId,
      incident_url: incidentUrl,
    },
  );

export const mergeIncidentsV2 = (
  envId: string,
  primaryIncidentId: string,
  incidentIds: string[],
  title?: string,
  userSummary?: string,
): Promise<MergeIncidentsV2Response> =>
  post<MergeIncidentsV2Response>(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${primaryIncidentId}/merge`,
    {
      incident_ids: incidentIds,
      title,
      user_summary: userSummary,
    },
  );

export const updateIncidentStatus = (
  envId: string,
  incidentIds: string[],
  status: string,
): Promise<void> =>
  put(`${window.VITE_API_BASE_URL}/${envId}/incidents/v2/status`, {
    incident_ids: incidentIds,
    status,
  });

export const updateIncidentAssignee = (
  envId: string,
  incidentIds: string[],
  assignee: string | null,
): Promise<void> =>
  put(`${window.VITE_API_BASE_URL}/${envId}/incidents/v2/assignee`, {
    incident_ids: incidentIds,
    assignee,
  });

export const updateIncidentSeverity = (
  envId: string,
  incidentIds: string[],
  severity: string,
): Promise<void> =>
  put(`${window.VITE_API_BASE_URL}/${envId}/incidents/v2/severity`, {
    incident_ids: incidentIds,
    severity,
  });

export const createIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
  content: string,
): Promise<SummaryResponse> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/summaries/${summaryType}`,
    { content },
  );

export const updateIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
  content: string,
): Promise<SummaryResponse> =>
  patch(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/summaries/${summaryType}`,
    { content },
  );

export const deleteIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
): Promise<void> =>
  del(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/summaries/${summaryType}`,
  );

export const splitIncidentV2 = (
  envId: string,
  incidentId: string,
  signalIds: string[],
): Promise<SplitIncidentV2Response> =>
  post<SplitIncidentV2Response>(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/v2/${incidentId}/split`,
    { signal_ids: signalIds },
  );
